/**
 * PhonePanel.jsx — CIVIX softphone slide-out panel
 * CIVIX dark theme: bg-civix-panel, civix-purple accent
 *
 * Tabs: PHONE · SEND TEXT · VOICEMAILS
 * Modular architecture: delegates to components/phone/* subcomponents
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Phone, PhoneOff, X, MessageSquare, Inbox, PhoneIncoming, ChevronLeft, ChevronRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiDel } from '../lib/api.js';
import { getSocket } from '../lib/socket.js';
import * as tv from '../lib/twilioVoice.js';
import { fmtPhone } from '../lib/format.js';
import { OPEN_TEXT_EVENT } from '../lib/phoneEvents.js';
import { usePowerDial } from '../state/powerDial.js';
import { powerdialApi } from '../lib/powerdial.js';
import { useDialGuards } from '../hooks/useDialGuards.jsx';
import CallScreenAlert from './compliance/CallScreenAlert.jsx';
import ConsentReminderModal from './ConsentReminderModal.jsx';
import PhoneTab from './phone/PhoneTab.jsx';
import TextTab from './phone/TextTab.jsx';
import VoicemailsTab from './phone/VoicemailsTab.jsx';
import DispositionModal from './phone/DispositionModal.jsx';
import ViewLeadPanel from './phone/ViewLeadPanel.jsx';

const PANEL_OPEN_KEY = 'civix_phone_panel_open';
const VIEW_LEAD_KEY = 'civix_view_lead_open';
let globalPanelToggle = null;
let globalSetIncoming = null;
let globalSetActiveCall = null;

// True while a multi-line Power Dial session is live. In that mode the agent's
// softphone leg is the conference "room" — hanging it up between leads ends the
// room, so lead hangups must drop only the lead leg (Wavv-style).
function isMultiLinePdActive() {
  const s = usePowerDial.getState().session;
  return Boolean(s?._id && (s.concurrency || 0) > 1 && ['queued', 'running', 'paused'].includes(s.status));
}

// Any live Power Dial session (single or multi-line). Its calls never open the
// centered disposition modal — the dialer keeps moving; dispositions stay
// available from the lead screen.
function isPdActive() {
  const s = usePowerDial.getState().session;
  return Boolean(s?._id && ['queued', 'running', 'paused'].includes(s.status));
}

export function openPhonePanel() {
  if (globalPanelToggle) globalPanelToggle(true);
}

export function setIncomingCallGlobal(data) {
  if (globalSetIncoming) globalSetIncoming(data);
}

export function setActiveCallGlobal(data) {
  if (globalSetActiveCall) globalSetActiveCall(data);
}

export default function PhonePanel() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(PANEL_OPEN_KEY) === 'true'; } catch { return false; }
  });
  const [tab, setTab] = useState('phone');
  const [dialInput, setDialInput] = useState('');
  const [callState, setCallState] = useState('idle');
  const { guardedClickToCall, guardModal } = useDialGuards();
  const [muted, setMuted] = useState(false);
  const [activeCallId, setActiveCallId] = useState(null);
  const [activeCallSid, setActiveCallSid] = useState(null);
  const [activeLead, setActiveLead] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [incomingCall, setIncomingCall] = useState(null);
  const [showDisposition, setShowDisposition] = useState(null);
  const [voiceReady, setVoiceReady] = useState(false);
  const [sdkBusy, setSdkBusy] = useState(false);
  const [callerIdOpen, setCallerIdOpen] = useState(false);
  // "View Lead" drawer beside the panel. focusedLeadId outlives the call so the
  // agent can finish notes / dispositions after hanging up.
  const [viewLeadOpen, setViewLeadOpen] = useState(() => {
    try { return localStorage.getItem(VIEW_LEAD_KEY) === 'true'; } catch { return false; }
  });
  const [focusedLeadId, setFocusedLeadId] = useState(null);
  const [viewLeadMode, setViewLeadMode] = useState('summary');

  const [showConsentReminder, setShowConsentReminder] = useState(false);
  const [consentAcking, setConsentAcking] = useState(false);
  const consentAckedRef = useRef(false);

  const [liveTurns, setLiveTurns] = useState([]);
  const [transcriptFading, setTranscriptFading] = useState(false);

  const [textTo, setTextTo] = useState('');
  const [textMsg, setTextMsg] = useState('');
  const [textSending, setTextSending] = useState(false);

  const [vmTab, setVmTab] = useState('inbox');
  const [vmUploadName, setVmUploadName] = useState('');
  const [vmUploading, setVmUploading] = useState(false);
  const vmFileRef = useRef();
  const durationRef = useRef(null);
  const activeCallSidRef = useRef(null);
  useEffect(() => { activeCallSidRef.current = activeCallSid; }, [activeCallSid]);
  // Refs so long-lived SDK listeners read the CURRENT call, not the mount-time value.
  const activeCallIdRef = useRef(null);
  const callDurationRef = useRef(0);
  useEffect(() => { activeCallIdRef.current = activeCallId; }, [activeCallId]);
  useEffect(() => { callDurationRef.current = callDuration; }, [callDuration]);

  const qc = useQueryClient();
  const pdSession = usePowerDial((s) => s.session);

  const { data: settingsData, refetch: refetchSettings } = useQuery({
    queryKey: ['phone-settings'],
    queryFn: () => apiGet('/phone/settings'),
    staleTime: 30000,
  });
  const settings = settingsData?.data?.settings || {};

  const { data: vmData, refetch: refetchVMs } = useQuery({
    queryKey: ['phone-voicemails'],
    queryFn: () => apiGet('/phone/voicemails'),
  });
  const drops = vmData?.data?.voicemails || [];

  const { data: inboxData, refetch: refetchInbox } = useQuery({
    queryKey: ['phone-inbox'],
    queryFn: () => apiGet('/phone/inbox/voicemails'),
    enabled: tab === 'voicemails' && vmTab === 'inbox',
  });
  const inboxVMs = inboxData?.data?.voicemails || [];

  const { data: callsData } = useQuery({
    queryKey: ['phone-calls'],
    queryFn: () => apiGet('/phone/calls'),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const recentCalls = callsData?.data?.calls || [];

  useEffect(() => {
    globalPanelToggle = setOpen;
    globalSetIncoming = setIncomingCall;
    globalSetActiveCall = (data) => {
      if (!data || data.action === 'clear' || data.callState === 'idle') {
        tv.hangUp();
        setCallState('idle');
        setActiveCallId(null);
        setActiveCallSid(null);
        setActiveLead(null);
        return;
      }
      if (activeCallSidRef.current) {
        if (data.callId) setActiveCallId((prev) => prev || data.callId);
        if (data.leadName) {
          setActiveLead((prev) => prev || { leadName: data.leadName, leadId: data.leadId || null, email: data.email || '', fromNumber: data.fromNumber || '' });
        }
        return;
      }
      setOpen(true);
      setTab('phone');
      const pd = usePowerDial.getState().session;
      if (data.leadId && pd?._id && ['queued', 'running', 'paused'].includes(pd.status)) setViewLeadOpen(true);
      setActiveCallId(data.callId ?? null);
      setActiveCallSid(data.callSid ?? null);
      setActiveLead({ leadName: data.leadName || 'Lead', leadId: data.leadId || null, email: data.email || '', fromNumber: data.fromNumber || '' });
      setCallState('in-progress');
    };
    return () => {
      globalPanelToggle = null;
      globalSetIncoming = null;
      globalSetActiveCall = null;
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(PANEL_OPEN_KEY, open ? 'true' : 'false'); } catch {}
  }, [open]);

  useEffect(() => {
    try { localStorage.setItem(VIEW_LEAD_KEY, viewLeadOpen ? 'true' : 'false'); } catch {}
  }, [viewLeadOpen]);

  useEffect(() => {
    if (activeLead?.leadId) setFocusedLeadId(String(activeLead.leadId));
  }, [activeLead?.leadId]);

  useEffect(() => {
    const mode = settings.mode || 'browser';
    if (mode !== 'browser') return;
    let cancelled = false;
    const offs = [];
    (async () => {
      try {
        const res = await apiPost('/phone/voice-token', {});
        if (cancelled) return;
        const token = res?.data?.token;
        if (!token) return;

        offs.push(tv.on('ready', () => {
          if (cancelled) return;
          setVoiceReady(true);
          tv.applySavedAudioDevices();
        }));
        offs.push(tv.on('error', (e) => { if (!cancelled) console.warn('Twilio Voice error:', e?.message || e || 'unknown error'); }));
        offs.push(tv.on('incoming', (data) => {
          if (cancelled) return;
          if (data?.autoAccept) {
            setActiveCallSid(data.callSid || null);
            setCallState('in-progress');
            return;
          }
          setIncomingCall({ ...data, fromSocket: false });
        }));
        offs.push(tv.on('ringing', () => { if (!cancelled) setCallState('ringing'); }));
        offs.push(tv.on('callAccepted', () => { if (!cancelled) setCallState('in-progress'); }));
        offs.push(tv.on('ended', ({ durationSec, reason } = {}) => {
          if (cancelled) return;
          // A multi-line Power Dial conference rejoin drops the old agent leg —
          // that is not the end of a lead call, so don't reset the panel.
          if (reason === 'recovered' && isMultiLinePdActive()) return;
          setCallState('ended');
          clearInterval(durationRef.current);
          const callId = activeCallIdRef.current;
          const dur = durationSec || callDurationRef.current;
          if (callId) {
            apiPost(`/phone/calls/${callId}/end`, { durationSec: dur }).catch(() => {});
            if (!isPdActive()) setShowDisposition({ callId, durationSec: dur });
          }
          qc.invalidateQueries({ queryKey: ['phone-calls'] });
          qc.invalidateQueries({ queryKey: ['call-history'] });
          setTimeout(() => { if (!cancelled) setCallState('idle'); }, 1500);
        }));
        offs.push(tv.on('muteChanged', ({ muted: m }) => { if (!cancelled) setMuted(m); }));
        offs.push(tv.on('tokenExpiring', async () => {
          try {
            const r = await apiPost('/phone/voice-token', {});
            if (r?.data?.token) await tv.initVoice(r.data.token);
          } catch (_) {}
        }));

        await tv.initVoice(token);
      } catch (e) {
        console.warn('Could not init Twilio Voice SDK:', e?.message || e || 'unknown error');
      }
    })();
    return () => {
      cancelled = true;
      offs.forEach((off) => { try { off?.(); } catch (_) {} });
    };
  }, [settings.mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const socket = getSocket();
    const onIncoming = (data) => {
      setIncomingCall(data);
      if (!open) {
        document.getElementById('phone-panel-btn')?.classList.add('animate-pulse');
        setTimeout(() => document.getElementById('phone-panel-btn')?.classList.remove('animate-pulse'), 5000);
      }
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Incoming Call', {
          body: `From: ${data.leadName || data.fromNumber}`,
          icon: '/favicon.ico',
        });
      }
    };
    const onStatus = (data) => {
      if (data.status === 'in-progress') setCallState('in-progress');
      else if (data.status === 'ringing') setCallState('ringing');
    };
    const onEnded = (data) => {
      setCallState('ended');
      clearInterval(durationRef.current);
      qc.invalidateQueries({ queryKey: ['phone-calls'] });
      const callId = data.callId || activeCallId;
      if (callId) {
        if (!isPdActive()) setShowDisposition({ callId, durationSec: data.durationSec || callDuration });
      }
      setTimeout(() => setCallState('idle'), 1500);
    };

    const onPdHangup = (payload) => {
      // Lead-leg hangup in multi-line: keep the agent in the conference room.
      const isLeadHangup = payload?.callSid && !payload?.reason && isMultiLinePdActive();
      if (!isLeadHangup) tv.hangUp();
      setCallState('ended');
      clearInterval(durationRef.current);
      qc.invalidateQueries({ queryKey: ['phone-calls'] });
      setTimeout(() => setCallState('idle'), 1000);
    };

    const onCallLogged = () => {
      qc.invalidateQueries({ queryKey: ['phone-calls'] });
    };

    socket.on('phone:incoming', onIncoming);
    socket.on('phone:status', onStatus);
    socket.on('phone:ended', onEnded);
    socket.on('powerdial:agent_hangup', onPdHangup);
    socket.on('powerdial:session:cancelled', onPdHangup);
    socket.on('powerdial:session:completed', onPdHangup);
    socket.on('call:logged', onCallLogged);
    return () => {
      socket.off('phone:incoming', onIncoming);
      socket.off('phone:status', onStatus);
      socket.off('phone:ended', onEnded);
      socket.off('powerdial:agent_hangup', onPdHangup);
      socket.off('powerdial:session:cancelled', onPdHangup);
      socket.off('powerdial:session:completed', onPdHangup);
      socket.off('call:logged', onCallLogged);
    };
  }, [open, activeCallId, callDuration, qc]);

  useEffect(() => {
    const socket = getSocket();
    const onTurn = (data) => {
      if (!data || !data.text) return;
      if (activeCallId && String(data.callId) !== String(activeCallId)) return;
      setLiveTurns((prev) => {
        const next = [...prev, {
          speaker: data.speaker === 'agent' ? 'agent' : 'prospect',
          text: data.text,
          ts: data.ts || new Date().toISOString(),
          callId: data.callId,
        }];
        return next.length > 30 ? next.slice(next.length - 30) : next;
      });
    };
    socket.on('human-call:turn', onTurn);
    return () => socket.off('human-call:turn', onTurn);
  }, [activeCallId]);

  useEffect(() => {
    if (callState === 'ended' && liveTurns.length > 0) {
      setTranscriptFading(true);
      const t = setTimeout(() => { setLiveTurns([]); setTranscriptFading(false); }, 10_000);
      return () => clearTimeout(t);
    }
    if (callState === 'in-progress' || callState === 'ringing' || callState === 'initiating') {
      setTranscriptFading(false);
    }
  }, [callState, liveTurns.length]);

  useEffect(() => {
    if (callState === 'in-progress') {
      setCallDuration(0);
      durationRef.current = setInterval(() => setCallDuration((d) => d + 1), 1000);
    } else {
      clearInterval(durationRef.current);
    }
    return () => clearInterval(durationRef.current);
  }, [callState]);

  useEffect(() => {
    if (callState === 'idle') {
      setActiveCallId(null);
      setActiveCallSid(null);
      setActiveLead(null);
    }
  }, [callState]);

  function formatDuration(sec) {
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  async function ensureConsentAcknowledged() {
    if (consentAckedRef.current) return true;
    try {
      const res = await apiGet('/agents/me/consent-reminder');
      if (res?.ok && res.acknowledgedToday) {
        consentAckedRef.current = true;
        return true;
      }
    } catch (_) {
      consentAckedRef.current = true;
      return true;
    }
    setShowConsentReminder(true);
    return false;
  }

  async function handleConsentAcknowledge() {
    setConsentAcking(true);
    try {
      await apiPost('/agents/me/consent-reminder/acknowledge');
    } catch (_) {}
    consentAckedRef.current = true;
    setConsentAcking(false);
    setShowConsentReminder(false);
    handleCall();
  }

  async function handleCall() {
    if (!dialInput.trim()) return;
    if (callState !== 'idle') return;
    if (!(await ensureConsentAcknowledged())) return;

    setSdkBusy(true);
    setCallState('initiating');

    try {
      const res = await guardedClickToCall({ phone: dialInput.trim() });
      if (res?.cancelled) {
        setCallState('idle');
        setSdkBusy(false);
        return;
      }
      if (!res?.ok) {
        toast.error(res?.error || 'Call failed');
        setCallState('idle');
        setSdkBusy(false);
        return;
      }

      const { mode, token, callContext } = res.data;
      setActiveCallId(callContext?.callId || null);
      // Show the matched lead (name + profile link) or the dialed number straight away.
      setActiveLead({
        leadName: callContext?.leadName || '',
        leadId: callContext?.leadId || null,
        email: callContext?.leadEmail || '',
        toNumber: callContext?.to || dialInput.trim(),
      });

      qc.invalidateQueries({ queryKey: ['call-history'] });
      qc.invalidateQueries({ queryKey: ['phone-calls'] });

      if (mode === 'browser') {
        if (token) {
          if (!tv.isReady()) await tv.initVoice(token);
          try {
            await tv.placeCall(dialInput.trim(), { callId: callContext?.callId || '' });
            setCallState('ringing');
          } catch (sdkErr) {
            const msg = String(sdkErr?.message || '').toLowerCase();
            if (msg.includes('already active') || msg.includes('already connected')) {
              tv.resetActiveCallState?.();
              await tv.placeCall(dialInput.trim(), { callId: callContext?.callId || '' });
              setCallState('ringing');
              toast.success('Softphone recovered — dialing');
            } else {
              throw sdkErr;
            }
          }
        }
      } else {
        setCallState('ringing');
        toast.success('Calling your cell phone to bridge the call…');
        setTimeout(() => setCallState('in-progress'), 3000);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Call failed');
      setCallState('idle');
    }
    setSdkBusy(false);
  }

  async function handleHangUp() {
    const multiLinePd = isMultiLinePdActive();
    if (!multiLinePd) tv.hangUp();
    setCallState('ended');
    clearInterval(durationRef.current);

    // In a Power Dial session the server finalizes the lead's PhoneCall row
    // (hangup-connected); /end here would double-write the duration.
    const pdSession = usePowerDial.getState().session;
    const pdActive = pdSession?._id && (pdSession.status === 'running' || pdSession.status === 'paused');
    if (activeCallId && !pdActive) {
      apiPost(`/phone/calls/${activeCallId}/end`, { durationSec: callDuration }).catch(() => {});
    }

    if (pdActive) {
      try {
        await powerdialApi.hangupConnected(pdSession._id);
      } catch (err) {
        console.warn('PowerDial hangup error:', err?.message || err);
      }
    }

    qc.invalidateQueries({ queryKey: ['phone-calls'] });
    qc.invalidateQueries({ queryKey: ['call-history'] });

    if (activeCallId && !isPdActive()) setShowDisposition({ callId: activeCallId, durationSec: callDuration });
    setTimeout(() => setCallState('idle'), 1500);
  }

  function handleMute() {
    const newMuted = !muted;
    tv.mute(newMuted);
    setMuted(newMuted);
  }

  /**
   * Play a saved drop to the lead, then the call wraps up. Browser calls have no
   * SID in state, so fall back to the SDK's live call and the PhoneCall id.
   * @param {{id: string, name: string}} vm
   */
  async function handleVoicemailDrop(vm) {
    const callSid = activeCallSid || tv.getActiveCall?.()?.parameters?.CallSid || null;
    if (!callSid && !activeCallId) { toast.error('No active call'); return; }
    try {
      await apiPost('/phone/voicemail-drop', { callSid, callId: activeCallId, voicemailId: vm.id });
      toast.success(`Voicemail "${vm.name}" dropped. Moving on…`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Drop failed');
    }
  }

  /**
   * Send (or schedule) a text. The API sends to a lead, so the Text tab resolves
   * the typed number to a lead first.
   * @param {{leadId: string, body: string, scheduledFor?: string}} payload
   * @returns {Promise<boolean>} True on success (the tab then clears itself).
   */
  async function handleSendText(payload) {
    if (!payload?.leadId || !payload?.body?.trim()) return false;
    setTextSending(true);
    try {
      await apiPost('/sms/send', payload);
      qc.invalidateQueries({ queryKey: ['scheduled-texts'] });
      qc.invalidateQueries({ queryKey: ['lead', payload.leadId] });
      return true;
    } catch (e) {
      toast.error(e.response?.data?.error || 'SMS failed');
      return false;
    } finally {
      setTextSending(false);
    }
  }

  async function handleVMUpload() {
    const file = vmFileRef.current?.files[0];
    if (!vmUploadName.trim()) { toast.error('Name required'); return; }
    if (!file) { toast.error('Select an audio file'); return; }
    if (file.size > 1_000_000) { toast.error('File too large (max 1 MB)'); return; }

    setVmUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        try {
          await apiPost('/phone/voicemails', {
            name: vmUploadName,
            audioBase64: base64,
            mimeType: file.type,
          });
          setVmUploadName('');
          vmFileRef.current.value = '';
          refetchVMs();
          toast.success('Voicemail drop saved');
        } catch (err) {
          toast.error(err.response?.data?.error || 'Upload failed');
        }
        setVmUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (e) {
      toast.error('Upload failed');
      setVmUploading(false);
    }
  }

  async function deleteVM(id) {
    try {
      await apiDel(`/phone/voicemails/${id}`);
      refetchVMs();
      toast.success('Deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  }

  async function handleAnswer() {
    tv.acceptIncoming();
    setOpen(true);
    setTab('phone');
    setActiveCallId(incomingCall?.callId || null);
    setActiveCallSid(incomingCall?.callSid || null);
    setCallState('in-progress');
    setIncomingCall(null);
  }

  function handleDecline() {
    tv.rejectIncoming();
    setIncomingCall(null);
  }

  const isInCall = ['initiating', 'ringing', 'in-progress'].includes(callState);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    const onToggle = () => setOpen((v) => !v);
    window.addEventListener('civix:phone:open', onOpen);
    window.addEventListener('civix:phone:close', onClose);
    window.addEventListener('civix:phone:toggle', onToggle);
    // "Send Text" from a lead profile: open the panel on the Text tab with that lead's number.
    const onText = (e) => {
      const phone = e?.detail?.phone;
      if (!phone) return;
      setOpen(true);
      setTab('text');
      setTextTo(String(phone));
    };
    window.addEventListener(OPEN_TEXT_EVENT, onText);
    return () => {
      window.removeEventListener(OPEN_TEXT_EVENT, onText);
      window.removeEventListener('civix:phone:open', onOpen);
      window.removeEventListener('civix:phone:close', onClose);
      window.removeEventListener('civix:phone:toggle', onToggle);
    };
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('civix:phone:state', {
      detail: { open, isInCall, hasIncoming: !!incomingCall, voiceReady },
    }));
  }, [open, isInCall, incomingCall, voiceReady]);

  return (
    <>
      {guardModal}
      {showConsentReminder && (
        <ConsentReminderModal
          busy={consentAcking}
          onAcknowledge={handleConsentAcknowledge}
        />
      )}

      {incomingCall && (
        <div className="fixed inset-0 z-200 flex items-center justify-center bg-black/60 backdrop-blur-xs">
          <div className="bg-civix-panel border border-civix-purple/60 rounded-2xl p-8 shadow-glow-purple w-80 text-center">
            <div className="w-16 h-16 rounded-full bg-civix-purple/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <PhoneIncoming className="w-8 h-8 text-civix-purple" />
            </div>
            <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Incoming Call</div>
            <div className="text-white font-display text-xl font-bold mb-1">
              {incomingCall.leadName || fmtPhone(incomingCall.fromNumber) || incomingCall.fromNumber}
            </div>
            {incomingCall.leadName && (
              <div className="text-white/50 text-sm mb-6">{fmtPhone(incomingCall.fromNumber)}</div>
            )}
            <div className="flex gap-4 justify-center mt-4">
              <button
                onClick={handleDecline}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/40 transition font-medium"
              >
                <PhoneOff className="w-5 h-5" /> Decline
              </button>
              <button
                onClick={handleAnswer}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/40 transition font-medium"
              >
                <Phone className="w-5 h-5" /> Answer
              </button>
            </div>
          </div>
        </div>
      )}

      {showDisposition && (
        <DispositionModal
          callId={showDisposition.callId}
          durationSec={showDisposition.durationSec}
          onClose={() => {
            setShowDisposition(null);
            window.dispatchEvent(new CustomEvent('civix:disposition:done'));
            qc.invalidateQueries({ queryKey: ['phone-calls'] });
            qc.invalidateQueries({ queryKey: ['call-history'] });
            qc.invalidateQueries({ queryKey: ['call-history-today'] });
          }}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full z-40 w-[360px] flex flex-col bg-civix-panel border-l border-civix-border shadow-panel transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* View / Hide Lead handle + lead drawer, docked to the panel's left edge */}
        <div className="hidden md:flex absolute top-0 right-full h-full items-stretch">
          <button
            onClick={() => setViewLeadOpen((v) => !v)}
            aria-expanded={viewLeadOpen}
            aria-label={viewLeadOpen ? 'Hide lead' : 'View lead'}
            data-testid="view-lead-toggle"
            className="self-center flex flex-col items-center gap-1 px-1.5 py-3 rounded-l-lg bg-civix-panelLight border border-r-0 border-civix-border text-[10px] font-bold tracking-wider text-civix-purpleSoft hover:text-white hover:bg-civix-purple/20 transition"
          >
            <span>{viewLeadOpen ? 'HIDE' : 'VIEW'}</span>
            {viewLeadOpen ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
            <span>LEAD</span>
          </button>
          {viewLeadOpen && (
            <div className={`${viewLeadMode === 'full' ? 'w-[640px]' : 'w-[440px]'} max-w-[calc(100vw-420px)] h-full transition-[width]`}>
              <ViewLeadPanel leadId={focusedLeadId} onClose={() => setViewLeadOpen(false)} mode={viewLeadMode} onModeChange={setViewLeadMode} />
            </div>
          )}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-civix-border bg-civix-panelLight">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-civix-purple/20 flex items-center justify-center">
              <Phone className="w-4 h-4 text-civix-purple" />
            </div>
            <span className="font-display font-semibold text-white text-sm">Phone Panel</span>
            {voiceReady ? (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] font-semibold">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>Ready</span>
              </span>
            ) : (settings.mode || 'browser') === 'browser' ? (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 text-[10px] font-semibold">
                <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                <span>Connecting…</span>
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/10 text-white/50 text-[10px]">
                <span>Cell Forward</span>
              </span>
            )}
          </div>
          <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-civix-border">
          {[
            { id: 'phone', icon: Phone, label: 'PHONE' },
            { id: 'text', icon: MessageSquare, label: 'SEND TEXT' },
            { id: 'voicemails', icon: Inbox, label: 'VOICEMAILS' },
          ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex-1 py-2.5 text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 border-b-2 transition ${
                tab === id
                  ? 'border-civix-purple text-civix-purpleSoft'
                  : 'border-transparent text-white/40 hover:text-white/70'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {id === 'voicemails' && inboxVMs.length > 0 && (
                <span className="w-4 h-4 rounded-full bg-civix-purple text-white text-[9px] flex items-center justify-center">
                  {inboxVMs.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Two-party consent state: legal recording disclosure reminder */}
        <CallScreenAlert callState={callState} leadId={activeLead?.leadId} phone={activeLead?.toNumber || dialInput} />

        {/* Power Dial status — mirrors the bottom run bar */}
        {pdSession && !['completed', 'cancelled'].includes(pdSession.status) && (
          <div className={`flex items-center justify-between gap-2 px-4 py-2 text-xs border-b border-civix-border ${
            pdSession.status === 'paused' ? 'bg-amber-500/10 text-amber-300' : 'bg-emerald-500/10 text-emerald-300'
          }`}>
            <span className="font-semibold">
              Power Dial · {pdSession.status === 'paused' ? 'Paused'
                : pdSession.connectedCallSid ? 'On call'
                : (pdSession.currentSet || []).some((m) => m.status === 'ringing') ? 'Ringing'
                : pdSession.status === 'queued' ? 'Starting…' : 'Dialing'}
            </span>
            <span className="text-white/50">
              {pdSession.results?.length || 0}/{pdSession.selectedLeadIds?.length || 0} · {pdSession.concurrency} lines
            </span>
          </div>
        )}

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'phone' && (
            <PhoneTab
              dialInput={dialInput}
              setDialInput={setDialInput}
              callState={callState}
              isInCall={isInCall}
              muted={muted}
              callDuration={callDuration}
              formatDuration={formatDuration}
              settings={settings}
              drops={drops}
              callerIdOpen={callerIdOpen}
              setCallerIdOpen={setCallerIdOpen}
              handleCall={handleCall}
              handleHangUp={handleHangUp}
              handleMute={handleMute}
              handleVoicemailDrop={handleVoicemailDrop}
              sdkBusy={sdkBusy}
              activeCallSid={activeCallSid}
              activeLead={activeLead}
              recentCalls={recentCalls}
              liveTurns={liveTurns}
              transcriptFading={transcriptFading}
              activeCallId={activeCallId}
              refetchSettings={refetchSettings}
            />
          )}
          {tab === 'text' && (
            <TextTab
              textTo={textTo}
              setTextTo={setTextTo}
              textMsg={textMsg}
              setTextMsg={setTextMsg}
              textSending={textSending}
              handleSendText={handleSendText}
              activeLead={activeLead}
            />
          )}
          {tab === 'voicemails' && (
            <VoicemailsTab
              vmTab={vmTab}
              setVmTab={setVmTab}
              inboxVMs={inboxVMs}
              drops={drops}
              vmUploadName={vmUploadName}
              setVmUploadName={setVmUploadName}
              vmUploading={vmUploading}
              vmFileRef={vmFileRef}
              handleVMUpload={handleVMUpload}
              deleteVM={deleteVM}
              refetchInbox={refetchInbox}
              onOpenLead={(id) => { setFocusedLeadId(String(id)); setViewLeadMode('full'); setViewLeadOpen(true); }}
            />
          )}
        </div>
      </div>
    </>
  );
}
